import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import { Avatar } from "V2.0/common/avatar";
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from "V2.0/common/dropdown";

import {
  Navbar,
  NavbarDivider,
  NavbarItem,
  NavbarLabel,
  NavbarSection,
  NavbarSpacer,
} from "V2.0/common/navbar";
import {
  Sidebar,
  SidebarBody,
  SidebarHeader,
  SidebarItem,
  SidebarLabel,
  SidebarSection,
} from "V2.0/common/sidebarCatalyst";
import { StackedLayout } from "V2.0/common/stacked-layout";

import {
  ArrowRightStartOnRectangleIcon,
  ChevronDownIcon,
  UserIcon,
} from "@heroicons/react/16/solid";

import UserManagement from "./Users";
import useCustomer from "V2.0/hooks/useCustomer";
import { useAccountContext } from "V2.0/Context/AccountContext";
import WorkspaceManagement from "./Workspaces";
import CompanyLogo from "V2.0/HomePages/Home/Sidebar/CompanyLogo";

const whitelabelOwnerAccountItems = [
  { label: "Team" },
  { label: "Workspaces" },
];

function Account() {
  const navigate = useNavigate();
  const [activeState, setActiveState] = useState("Team");

  const { isLoading, isOwner, name, email } = useCustomer();
  const { company, isAccountLoading } = useAccountContext();

  const navItems = whitelabelOwnerAccountItems;

  if (isLoading || isAccountLoading) {
    return null;
  }

  if (!isOwner || !company?._id) {
    return <Navigate to="/" replace />;
  }

  return (
    <StackedLayout
      navbar={
        <Navbar>
          <Dropdown>
            <DropdownButton
              as={NavbarItem}
              className="max-lg:hidden"
              onClick={() => navigate("/")}
            >
              <CompanyLogo company={company} />
            </DropdownButton>
          </Dropdown>
          <NavbarDivider className="max-lg:hidden" />
          <NavbarSection className="max-lg:hidden">
            {navItems.map(({ label, url }) => (
              <NavbarItem
                key={label}
                onClick={() => {
                  setActiveState(label); // Set active state on click
                }}
                current={activeState === label}
              >
                {label}
              </NavbarItem>
            ))}
          </NavbarSection>
          <NavbarSpacer />
          <NavbarSection>
            <Dropdown>
              <DropdownButton as={NavbarItem}>
                <Avatar
                  initials={name.slice(0, 2)}
                  className="size-10 bg-zinc-900 text-white dark:bg-white dark:text-black"
                />
              </DropdownButton>
              <DropdownMenu className="min-w-64" anchor="bottom end">
                <DropdownItem to="/dashboard">
                  <UserIcon />
                  <DropdownLabel>Home</DropdownLabel>
                </DropdownItem>
                <DropdownDivider />
                <DropdownItem to="/logout">
                  <ArrowRightStartOnRectangleIcon />
                  <DropdownLabel>Sign out</DropdownLabel>
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </NavbarSection>
        </Navbar>
      }
      sidebar={
        <Sidebar>
          <SidebarHeader>
            <Dropdown>
              <DropdownButton as={SidebarItem} className="lg:mb-2.5">
                <div className="flex items-center my-0">
                  <CompanyLogo company={company} />
                </div>
                <ChevronDownIcon />
              </DropdownButton>
            </Dropdown>
          </SidebarHeader>
          <SidebarBody>
            <SidebarSection>
              {navItems.map(({ label, url }) => (
                <SidebarItem key={label} current={activeState === label}>
                  {label}
                </SidebarItem>
              ))}
            </SidebarSection>
          </SidebarBody>
        </Sidebar>
      }
    >
      {activeState === "Team" && <UserManagement />}
      {activeState === "Workspaces" && <WorkspaceManagement />}
    </StackedLayout>
  );
}

export default Account;
